import type { Page } from '@outfalak/shared'

/**
 * What the `/v1/abandoned` endpoints answer with. Mirrors
 * `apps/api/src/recovery/*` — the server is the only place any of these
 * numbers are computed; the screen renders them.
 */

export type Channel = 'whatsapp' | 'email'

export interface Filters {
  recovery: 'open' | 'recovered' | 'all'
  q?: string
  name?: string
  phone?: string
  email?: string
  product?: string
  valueMin?: number
  valueMax?: number
  from?: string
  to?: string
  withinHours?: number
  whatsapp?: 'eligible' | 'ineligible'
  emailStatus?: 'has_email' | 'no_email' | 'eligible' | 'emailed' | 'not_emailed'
  remindersMin?: number
  remindersMax?: number
  payment?: 'none' | 'pending' | 'failed'
  customerType?: 'new' | 'returning'
  clicked?: 'yes' | 'no'
  verified?: 'yes' | 'no'
  segment?: SegmentKey
  store?: string
}

export type SortKey = 'at' | 'created' | 'value' | 'name' | 'reminders' | 'clicks'

export type SegmentKey =
  | 'high_value'
  | 'recent'
  | 'never_contacted'
  | 'payment_failed'
  | 'returning'
  | 'reminder_due'
  | 'clicked_not_recovered'
  | 'all_eligible'

export interface RecoverableRow {
  id: string
  shopId: string
  store: string | null
  customerName: string | null
  phone: string | null
  email: string | null
  verified: boolean
  value: string
  items: number
  product: string | null
  stage: string
  attempts: number
  at: string
  createdAt: string
  recovered: boolean
  paymentStatus: 'none' | 'pending' | 'failed'
  returning: boolean
  canRemind: boolean
  hasEmail: boolean
  whatsappEligible: boolean
  emailEligible: boolean
  reminded: number
  whatsappSent: number
  emailSent: number
  lastContactedAt: string | null
  clickedAt: string | null
  clicks: number
  optedOut: boolean
}

export interface Summary {
  openCount: number
  openValue: string
  recoveredCount: number
  recoveredValue: string
  recoveryRate: number | null
}

export interface ListPayload extends Page<RecoverableRow> {
  summary: Summary
  access: { canWrite: boolean; masked: boolean; multiStore: boolean }
}

/** What the merchant has ticked, in the shape the server resolves. */
export type Selection = { mode: 'ids'; ids: string[] } | { mode: 'filter'; filters: Filters; excludeIds: string[] }

export interface SelectionPreview {
  total: number
  eligible: number
  ineligible: number
  reasons: Record<string, number>
  reasonLabels: Record<string, string>
  sampleEligible: Array<{ id: string; name: string | null; value: string }>
  sampleIneligible: Array<{ id: string; name: string | null; reason: string }>
  pricePerMessage: string
  estimatedCost: string
  walletBalance: string | null
  walletCovers: boolean
  representativeId: string | null
  maxSelection: number
}

export type Slot = { source: 'variable'; key: string } | { source: 'text'; value: string }

export interface WhatsappContent {
  template: string
  language: string
  variables: Slot[]
}

export interface EmailContent {
  subject: string
  body: string
}

export interface ApprovedTemplate {
  name: string
  language: string | null
  status: string
  category: string | null
  variables: number | null
  headerTakesValue: boolean
  urlButton: { index: number; prefix: string | null } | null
  body: string | null
}

export interface TemplatesPayload {
  whatsapp: { configured: boolean; error: string | null; defaultTemplate: string | null; language: string; templates: ApprovedTemplate[] }
  email: { configured: boolean; templates: Array<{ key: string; name: string; subject: string; body: string }> }
  variables: Array<{ key: string; label: string; sample: string }>
  prices: { whatsapp: string; email: string }
  ai: boolean
}

export interface PreviewPayload {
  errors: string[]
  whatsapp?: { template: string; params: string[]; text: string | null }
  email?: { subject: string; text: string; html: string }
}

export interface CampaignView {
  id: string
  name: string
  kind: string
  channel: Channel
  status: 'scheduled' | 'running' | 'completed' | 'cancelled'
  couponCode: string | null
  recipientCount: number
  estimatedCost: string
  scheduledAt: string
  startedAt: string | null
  completedAt: string | null
  createdAt: string
  counts: Record<'queued' | 'processing' | 'sent' | 'delivered' | 'read' | 'failed' | 'skipped' | 'cancelled', number>
  clicked: number
  spent: string
}

export interface WorkflowStep {
  id: 's1' | 's2' | 's3'
  delayMinutes: number
  channel: Channel
  whatsapp?: WhatsappContent
  email?: EmailContent
  couponCode?: string | null
}

export interface WorkflowConfig {
  status: 'active' | 'paused'
  steps: WorkflowStep[]
  quietHours: { enabled: boolean; start: string; end: string; timezone: string }
  maxRetries: number
  cooldownHours: number
}

export interface WorkflowPayload {
  exists: boolean
  config: WorkflowConfig
  activatedAt: string | null
  lastRunAt: string | null
  stepCounts: Array<{ step: string | null; status: string; count: number }>
  history: Array<{ id: string; checkoutId: string | null; step: string | null; channel: string; status: string; reason: string | null; at: string }>
}

export interface Metrics {
  sent: number
  failed: number
  skipped: number
  delivered: number
  read: number
  clicked: number
  contacted: number
  deliveryRate: number | null
  readRate: number | null
  clickRate: number | null
  recoveredOrders: number
  recoveredRevenue: string
  recoveryRate: number | null
  messagingCost: string
  netRecovery: string | null
  byChannel: Record<Channel, { sent: number; recovered: number; revenue: string; cost: string }>
  daily: Array<{ day: string; sent: number; recovered: number }>
}

export interface AnalyticsPayload {
  range: { from: string; to: string }
  attributionWindowDays: number
  receiptsConfigured: boolean
  abandoned: { total: number; recoverableValue: string }
  metrics: Metrics
  campaigns: Array<{ id: string; name: string }>
}

export interface CheckoutDetail {
  checkout: RecoverableRow & { status: string; couponCode: string | null; marketingConsent: boolean | null }
  cart: Array<{ title: string; variant: string | null; quantity: number; price: string | null; image: string | null }>
  totals: { subtotal: string | null; shipping: string | null; discount: string | null; total: string }
  eligibility: Record<Channel, { eligible: boolean; reason: string | null }>
  suppressed: Record<Channel, boolean>
  messages: Array<{
    id: string
    channel: string
    status: string
    source: string
    template: string | null
    sentAt: string | null
    deliveredAt: string | null
    readAt: string | null
    failedAt: string | null
    clickedAt: string | null
    clicks: number
    cost: string
    error: string | null
    skipReason: string | null
  }>
  timeline: Array<{ at: string; kind: string; label: string; detail: string | null; tone: string | null }>
  orders: Array<{ id: string; orderNumber: string; total: string; paymentStatus: string; createdAt: string; sameCheckout: boolean }>
  access: { canWrite: boolean; masked: boolean }
}

export const SEGMENT_LABEL: Record<SegmentKey, string> = {
  high_value: 'High-value carts',
  recent: 'Recently abandoned',
  never_contacted: 'Never contacted',
  payment_failed: 'Payment failed',
  returning: 'Returning customers',
  reminder_due: 'Reminder due',
  clicked_not_recovered: 'Clicked, not recovered',
  all_eligible: 'All eligible recipients',
}

export const MESSAGE_TONE: Record<string, string> = {
  queued: 'outline',
  processing: 'info',
  sent: 'info',
  delivered: 'ok',
  read: 'ok',
  failed: 'bad',
  skipped: 'warn',
  cancelled: 'warn',
}

/** A fresh key per submit attempt — a retried request reuses it, a new action does not. */
export function newIdempotencyKey(prefix: string): string {
  const random = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`
  return `${prefix}-${random}`.replace(/[^A-Za-z0-9:_-]/g, '').slice(0, 80)
}

export function downloadCsv(filename: string, csv: string): void {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}
